import {
  SelectOptionType,
  FormFieldValidationType,
  FormFieldStateType,
  CUSTOM_VALIDATION_TYPES,
} from "src/forms/types";
import {
  SITE_CONFIGURATION_CONSTANTS,
  GENERAL_FORM_CONSTANTS,
} from "src/forms/constants";
import { USA_STATES } from "src/forms/options";
import { CANADA_PROVINCES } from "src/forms/WorkersCompensation/sedgwickCanadaOptions";
import { isEqual } from "lodash";
import { ISO_COUNTRY_CODES } from "src/app/constants";

interface CountryConfigEntry {
  label: string;
  regions: SelectOptionType[];
  postalCodeValidation: FormFieldValidationType;
  /** Whether email notification list and slack webhook are required for site submission. */
  notificationsRequired: boolean;
}

/**
 * Normalizes a country code to its ISO 3166-1 alpha-2 form.
 * Handles the legacy "CAN" format for backward compatibility with existing data.
 */
export const normalizeCountryCode = (country: string): string =>
  country.toUpperCase() === "CAN"
    ? ISO_COUNTRY_CODES.CANADA
    : country.toUpperCase();

/**
 * Country configuration map for site configuration.
 * To add a new country, add an entry with:
 *   - label: display name for the country dropdown
 *   - regions: the list of state/province options for that country
 *   - postalCodeValidation: validation rules for the postal/zip code field
 *   - notificationsRequired: whether email alias list and slack webhook are mandatory
 */
export const COUNTRY_CONFIG: Record<string, CountryConfigEntry> = {
  [ISO_COUNTRY_CODES.UNITED_STATES]: {
    label: "United States",
    regions: USA_STATES,
    notificationsRequired: true,
    postalCodeValidation: {
      required: { value: true },
      matchRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ZIP_CODE_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_MESSAGE,
      },
      allowedCharactersRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX),
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: {
        value: 5,
        message: GENERAL_FORM_CONSTANTS.ZIP_CODE_MAX_CHARACTERS_MESSAGE,
      },
    },
  },
  [ISO_COUNTRY_CODES.CANADA]: {
    label: "Canada",
    regions: CANADA_PROVINCES,
    notificationsRequired: false,
    postalCodeValidation: {
      required: { value: true },
      matchRegExp: {
        value: new RegExp(GENERAL_FORM_CONSTANTS.CANADA_POSTAL_CODE_REGEX),
        message: GENERAL_FORM_CONSTANTS.CANADA_POSTAL_CODE_INVALID_MESSAGE,
      },
      allowedCharactersRegExp: {
        value: new RegExp(
          GENERAL_FORM_CONSTANTS.CANADA_POSTAL_CODE_ALLOWED_CHARACTERS_REGEX
        ),
        message:
          GENERAL_FORM_CONSTANTS.CANADA_POSTAL_CODE_INVALID_CHARACTERS_MESSAGE,
      },
      maxCharacters: {
        value: 7,
        message:
          GENERAL_FORM_CONSTANTS.CANADA_POSTAL_CODE_MAX_CHARACTERS_MESSAGE,
      },
    },
  },
};

// Derived from COUNTRY_CONFIG: maps each country code to a { value, label } select option.
// e.g. [{ value: "US", label: "United States" }, { value: "CA", label: "Canada" }]
export const COUNTRY_OPTIONS: SelectOptionType[] = Object.entries(
  COUNTRY_CONFIG
).map(([code, { label }]) => ({ value: code, label }));

/**
 * Find a region (state/province) across all configured countries.
 */
export const findRegion = (
  regionCode: string
): SelectOptionType | undefined => {
  for (const { regions } of Object.values(COUNTRY_CONFIG)) {
    const match = regions.find((r) => r.value === regionCode);
    if (match) return match;
  }
  return undefined;
};

/**
 * Builds form field state overrides for a given field key when loading a site.
 * Handles value mapping (state lookup, country label, alias list parsing)
 * and sets disabled state for auto-populated fields.
 */
export const buildSiteFieldOverrides = (
  key: string,
  currentSiteValue: any,
  countryCode: string
): Partial<FormFieldStateType> => {
  const countryConfig = COUNTRY_CONFIG[normalizeCountryCode(countryCode)];

  switch (key) {
    case SITE_CONFIGURATION_CONSTANTS.STATE:
      return {
        value: findRegion(currentSiteValue as string),
        options: countryConfig?.regions,
      };

    case SITE_CONFIGURATION_CONSTANTS.COUNTRY:
      return {
        value: countryConfig
          ? { value: countryCode, label: countryConfig.label }
          : { value: countryCode, label: countryCode },
        options: COUNTRY_OPTIONS,
      };

    case SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_ALIAS_LIST: {
      const value: { value: string; errors: never[] }[] = [];
      if (Array.isArray(currentSiteValue)) {
        for (const alias of currentSiteValue) {
          value.push({ value: alias, errors: [] });
        }
      }
      return { value };
    }

    case SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY:
    case SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY:
      return { disabled: true };

    default:
      return {};
  }
};

/**
 * Returns country-dependent form field state overrides (options, validationOverride)
 * for all fields affected by the selected country. Call this on site load AND
 * whenever the country dropdown value changes.
 */
export const getCountryDependentOverrides = (
  countryCode: string
): Record<string, Partial<FormFieldStateType>> => {
  const countryConfig = COUNTRY_CONFIG[normalizeCountryCode(countryCode)];
  const notificationsRequired = countryConfig?.notificationsRequired ?? true;

  return {
    [SITE_CONFIGURATION_CONSTANTS.STATE]: {
      options: countryConfig?.regions,
    },
    [SITE_CONFIGURATION_CONSTANTS.ZIP_CODE]: {
      ...(countryConfig?.postalCodeValidation && {
        validationOverride: countryConfig.postalCodeValidation,
      }),
    },
    [SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_ALIAS_LIST]: {
      validationOverride: notificationsRequired
        ? undefined
        : {
            required: { value: false },
            matchRegExp: {
              value: new RegExp(/^(?!.*@).*$/),
              message: `Please enter the email alias without "@amazon.com".`,
            },
            changeToLowerCase: { value: true },
            maxCharacters: { value: 50 },
            minCharacters: { value: 3 },
            [CUSTOM_VALIDATION_TYPES.MAX_ATTRIBUTES]: {
              value: 5,
              message:
                "A maximum of 5 email aliases are supported. Please limit audience to need to know stakeholders",
            },
            [CUSTOM_VALIDATION_TYPES.MIN_ATTRIBUTES]: {
              value: 0,
              message: "",
            },
            [CUSTOM_VALIDATION_TYPES.DOES_NOT_START_WITH]: {
              value: "workcomp",
              message: `Please enter an email alias that does not start with "workcomp"`,
            },
            [CUSTOM_VALIDATION_TYPES.DUPLICATE_ATTRIBUTES_NOT_ALLOWED]: {
              value: true,
              message: "Duplicate aliases are not allowed",
            },
            [CUSTOM_VALIDATION_TYPES.REMOVE_WHITE_SPACE]: { value: true },
            [CUSTOM_VALIDATION_TYPES.DIFFERENT_THAN_SUBMITTER_ALIAS]: {
              value: true,
              message: "Product group user aliases are not allowed.",
            },
          },
    },
    [SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY]: {
      validationOverride: notificationsRequired
        ? undefined
        : { required: { value: false } },
    },
    [SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY]: {
      validationOverride: notificationsRequired
        ? undefined
        : { required: { value: false } },
    },
  };
};

interface NotificationContext {
  countryCode: string | undefined;
  notificationAliasList: any;
  notificationWebhook: any;
  emailNotificationRequestedBy: FormFieldStateType;
  notificationWebhookRequestedBy: FormFieldStateType;
  isEditMode: boolean;
  originalAliasList: string[] | undefined;
  originalWebhook: string | undefined;
}

const REQUESTED_BY_BASE_VALIDATION: Omit<FormFieldValidationType, "required"> =
  {
    maxCharacters: { value: 11 },
    allowedCharactersRegExp: {
      value: new RegExp(GENERAL_FORM_CONSTANTS.ONLY_LETTERS_AND_NUMBERS_REGEX),
      message: "Please enter only numbers and letters",
    },
  };

/**
 * Computes form state updates for notification "requested by" fields based on
 * whether notification values have changed and whether notifications are required
 * for the current country.
 */
export const getNotificationFieldUpdates = (
  ctx: NotificationContext
): Record<string, Partial<FormFieldStateType>> => {
  const countryConfig = ctx.countryCode
    ? COUNTRY_CONFIG[normalizeCountryCode(ctx.countryCode)]
    : undefined;
  const notificationsRequired = countryConfig?.notificationsRequired ?? true;

  const aliasValues = Array.isArray(ctx.notificationAliasList)
    ? ctx.notificationAliasList.map((item: any) => item.value)
    : [];
  const aliasHasValues = aliasValues.length > 0;
  const webhookHasValue = !!ctx.notificationWebhook;

  const hasAliasChanges = ctx.isEditMode
    ? !isEqual(aliasValues, ctx.originalAliasList)
    : aliasHasValues;
  const hasWebhookChanges = ctx.isEditMode
    ? !isEqual(ctx.notificationWebhook, ctx.originalWebhook)
    : webhookHasValue;

  const updates: Record<string, Partial<FormFieldStateType>> = {};

  if (hasAliasChanges && ctx.emailNotificationRequestedBy.disabled) {
    updates[SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY] = {
      ...ctx.emailNotificationRequestedBy,
      disabled: false,
      value: "",
    };
  }

  if (hasWebhookChanges && ctx.notificationWebhookRequestedBy.disabled) {
    updates[SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY] = {
      ...ctx.notificationWebhookRequestedBy,
      disabled: false,
      value: "",
    };
  }

  if (!notificationsRequired) {
    const emailKey =
      SITE_CONFIGURATION_CONSTANTS.EMAIL_NOTIFICATION_REQUESTED_BY;
    const webhookKey =
      SITE_CONFIGURATION_CONSTANTS.NOTIFICATION_WEBHOOK_REQUESTED_BY;
    updates[emailKey] = {
      ...(updates[emailKey] || ctx.emailNotificationRequestedBy),
      validationOverride: {
        required: { value: notificationsRequired || aliasHasValues },
        ...REQUESTED_BY_BASE_VALIDATION,
      },
    };
    updates[webhookKey] = {
      ...(updates[webhookKey] || ctx.notificationWebhookRequestedBy),
      validationOverride: {
        required: { value: notificationsRequired || webhookHasValue },
        ...REQUESTED_BY_BASE_VALIDATION,
      },
    };
  }

  return updates;
};
